import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Alert,
} from '@mui/material';
import { LeadMagnetSettings } from '../../services/landingPageApi';

interface LeadMagnetTabProps {
  data: LeadMagnetSettings;
  onSave: (updates: Partial<LeadMagnetSettings>) => void;
  isSaving: boolean;
}

export const LeadMagnetTab = ({ data, onSave, isSaving }: LeadMagnetTabProps) => {
  const [formData, setFormData] = useState<LeadMagnetSettings>({
    enabled: true,
    headline: '',
    subheadline: '',
    buttonText: '',
    thankYouMessage: '',
    aiContext: '',
  });

  useEffect(() => {
    setFormData({
      enabled: data.enabled ?? true,
      headline: data.headline || "What's Taking Up Too Much of Your Time?",
      subheadline: data.subheadline || "Share your biggest challenges, and I'll send you a personalized action plan — free.",
      buttonText: data.buttonText || 'Get My Free Action Plan',
      thankYouMessage: data.thankYouMessage || 'Thanks! Check your email for your personalized action plan.',
      aiContext: data.aiContext || '',
    });
  }, [data]);

  const handleChange = (field: keyof LeadMagnetSettings) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, enabled: e.target.checked });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ mb: 4 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.enabled}
              onChange={handleSwitchChange}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Enable Lead Magnet Widget
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Capture leads with an AI-powered action plan generator
              </Typography>
            </Box>
          }
        />
      </Box>

      {formData.enabled && (
        <>
          <Alert severity="info" sx={{ mb: 4 }}>
            When visitors submit their challenges, they'll automatically receive a personalized action plan generated by AI and sent to their email.
          </Alert>

          <Typography variant="h6" sx={{ mb: 3 }}>
            Widget Text
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Headline"
                value={formData.headline}
                onChange={handleChange('headline')}
                fullWidth
                placeholder="What's Taking Up Too Much of Your Time?"
                helperText="The main question to ask visitors"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Subheadline"
                value={formData.subheadline}
                onChange={handleChange('subheadline')}
                fullWidth
                multiline
                rows={2}
                placeholder="Share your biggest challenges, and I'll send you a personalized action plan — free."
                helperText="Supporting text explaining the offer"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Button Text"
                value={formData.buttonText}
                onChange={handleChange('buttonText')}
                fullWidth
                placeholder="Get My Free Action Plan"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Thank You Message"
                value={formData.thankYouMessage}
                onChange={handleChange('thankYouMessage')}
                fullWidth
                placeholder="Thanks! Check your email..."
                helperText="Shown after successful submission"
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mt: 4, mb: 3 }}>
            AI Personalization
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="AI Context"
                value={formData.aiContext}
                onChange={handleChange('aiContext')}
                fullWidth
                multiline
                rows={4}
                placeholder="Example: I specialize in email management, social media scheduling, and calendar management. I work primarily with coaches and consultants. My approach focuses on creating systems and processes..."
                helperText={`Help the AI understand your services for better personalized responses. ${formData.aiContext?.length || 0}/500 characters`}
                inputProps={{ maxLength: 500 }}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isSaving}
          sx={{ px: 4 }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};
